import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import storage from './storage'; // Import our storage abstraction

// Create a custom Axios instance with default configuration
const oneSpaceAxios = axios.create({
  timeout: 15000, // 15 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Type for the onespace API options
interface OnespaceApiOptions extends AxiosRequestConfig {
  skipAuth?: boolean;
  skipTenant?: boolean;
}

// Event system for offline detection
type OfflineEventListener = (error: {
  isOffline: boolean;
  errorMessage: string;
  originalError: any;
}) => void;

class OfflineEventEmitter {
  private listeners: OfflineEventListener[] = [];

  addListener(listener: OfflineEventListener) {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  emit(error: { isOffline: boolean; errorMessage: string; originalError: any }) {
    this.listeners.forEach(listener => listener(error));
  }
}

export const offlineEvents = new OfflineEventEmitter();

// Helper function to determine if error should trigger offline state
const isOfflineError = (error: any): { isOffline: boolean; errorMessage: string } => {
  // Check network-related errors
  if (error.code === 'NETWORK_ERROR' || 
      error.code === 'ERR_NETWORK' || 
      error.code === 'ERR_INTERNET_DISCONNECTED' ||
      error.message?.includes('Network Error') ||
      error.message?.includes('Failed to fetch') ||
      !navigator.onLine) {
    return {
      isOffline: true,
      errorMessage: 'Geen internetverbinding beschikbaar'
    };
  }

  // Check HTTP response errors
  if (error.response && error.response.status) {
    const status = error.response.status;
    
    if (status >= 500) {
      return {
        isOffline: true,
        errorMessage: 'Server tijdelijk niet beschikbaar'
      };
    } else if (status >= 400) {
      return {
        isOffline: true,
        errorMessage: 'Er is een probleem opgetreden met de verbinding'
      };
    }
  }

  // Check timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return {
      isOffline: true,
      errorMessage: 'Verbinding verlopen - controleer uw internetverbinding'
    };
  }

  // Default for unknown errors that might indicate connectivity issues
  return {
    isOffline: true,
    errorMessage: 'Kan geen verbinding maken met de server'
  };
};

// Intercept all requests to add auth and tenant headers
oneSpaceAxios.interceptors.request.use(async (config) => {
  // Get the combined localStorage JSON string
  const localStorageData = await storage.getItem('localStorage');
  
  let token = null;
  let tenantUuid = null;
  let apiUrl = null;
  
  // Parse the JSON string if it exists
  if (localStorageData) {
    try {
      const parsedData = JSON.parse(localStorageData);
      token = parsedData.auth_token;
      tenantUuid = parsedData.tenant_uuid;
      apiUrl = parsedData.tenant_api;
      
      console.log('Retrieved auth data from combined localStorage:', { 
        hasToken: !!token, 
        hasTenant: !!tenantUuid, 
        hasApi: !!apiUrl 
      });
    } catch (error) {
      console.error('Error parsing localStorage JSON:', error);
    }
  }

  // If we couldn't get the values from localStorage JSON, try the individual keys
  if (!token || !tenantUuid || !apiUrl) {
    // First priority: check for new keys with onespace_ prefix
    token = token || await storage.getItem('onespace_token');
    tenantUuid = tenantUuid || await storage.getItem('onespace_tenant_uuid');
    apiUrl = apiUrl || await storage.getItem('onespace_external_api');
    
    // If still not found, try legacy keys without prefix
    if (!token || !tenantUuid || !apiUrl) {
      token = token || await storage.getItem('auth_token');
      tenantUuid = tenantUuid || await storage.getItem('tenant_uuid');
      apiUrl = apiUrl || await storage.getItem('tenant_api');
    }
    
    // console.log('Retrieved auth data from individual keys:', {
    //   hasToken: !!token,
    //   hasTenant: !!tenantUuid,
    //   hasApi: !!apiUrl
    // });
  }
  
  // Set base URL from tenant_api if available
  if (apiUrl && !config.baseURL) {
    config.baseURL = apiUrl;
  }

  // Add auth token if available and not skipped
  if (token && !config.headers['Authorization'] && !(config as any).skipAuth) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add tenant ID if available and not skipped
  if (tenantUuid && !config.headers['X-Tenant-ID'] && !(config as any).skipTenant) {
    config.headers['X-Tenant-ID'] = tenantUuid;
  }
  
  // Remove custom options before sending request
  if ((config as any).skipAuth) {
    delete (config as any).skipAuth;
  }
  
  if ((config as any).skipTenant) {
    delete (config as any).skipTenant;
  }
  
  return config;
});

// Add response interceptor for centralized error handling
oneSpaceAxios.interceptors.response.use(
  (response) => {
    // Response successful - clear any offline state
    offlineEvents.emit({
      isOffline: false,
      errorMessage: '',
      originalError: null
    });
    return response;
  },
  (error) => {
    // Determine if this error should trigger offline state
    const offlineInfo = isOfflineError(error);
    
    console.error('API Error:', error);
    console.log('Offline Info:', offlineInfo);
    
    // Emit offline event
    offlineEvents.emit({
      isOffline: offlineInfo.isOffline,
      errorMessage: offlineInfo.errorMessage,
      originalError: error
    });
    
    // Still reject the promise so the calling code can handle it
    return Promise.reject(error);
  }
);

// Onespace API wrapper
const oneSpaceApi = {
  /**
   * Make a GET request to the Onespace API
   * @param url The endpoint URL (will be appended to the base URL)
   * @param options Additional Axios request options
   */
  get: <T = any>(url: string, options?: OnespaceApiOptions): Promise<AxiosResponse<T>> => {
    return oneSpaceAxios.get<T>(url, options);
  },
  
  /**
   * Make a POST request to the Onespace API
   * @param url The endpoint URL (will be appended to the base URL)
   * @param data The request body data
   * @param options Additional Axios request options
   */
  post: <T = any>(url: string, data?: any, options?: OnespaceApiOptions): Promise<AxiosResponse<T>> => {
    return oneSpaceAxios.post<T>(url, data, options);
  },
  
  /**
   * Make a PUT request to the Onespace API
   * @param url The endpoint URL (will be appended to the base URL)
   * @param data The request body data
   * @param options Additional Axios request options
   */
  put: <T = any>(url: string, data?: any, options?: OnespaceApiOptions): Promise<AxiosResponse<T>> => {
    return oneSpaceAxios.put<T>(url, data, options);
  },
  
  /**
   * Make a DELETE request to the Onespace API
   * @param url The endpoint URL (will be appended to the base URL)
   * @param options Additional Axios request options
   */
  delete: <T = any>(url: string, options?: OnespaceApiOptions): Promise<AxiosResponse<T>> => {
    return oneSpaceAxios.delete<T>(url, options);
  },
  
  /**
   * Make a PATCH request to the Onespace API
   * @param url The endpoint URL (will be appended to the base URL)
   * @param data The request body data
   * @param options Additional Axios request options
   */
  patch: <T = any>(url: string, data?: any, options?: OnespaceApiOptions): Promise<AxiosResponse<T>> => {
    return oneSpaceAxios.patch<T>(url, data, options);
  },
  
  /**
   * Get the underlying Axios instance for advanced usage
   */
  getInstance: () => {
    return oneSpaceAxios;
  }
};

export default oneSpaceApi; 